import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import LeftBar from '../Components/LeftBar'
import BottomNav from '../Components/BottomNav'
import Topbar from '../Components/Topbar'
import { Tab, Tabs, Spinner } from 'react-bootstrap'
import { getFirestore, collection, getDocs, query, where, orderBy } from 'firebase/firestore'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { app } from '../firebase'
import VerificationStatus from './VerificationStatus'
import noSubs from '../Images/mwanadamu.png'

function Earnings() {
    const db = getFirestore(app)
    const auth = getAuth()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [groupedIncome, setGroupedIncome] = useState({ adRequest: [], subscription: [] })
    const [userId, setUserId] = useState(null)

    // ====== SEO & Meta Tags ======
    useEffect(() => {
        // Page title
        document.title = "Zindua App | View Earnings";

        // Meta description
        let desc = document.querySelector("meta[name='description']");
        if (!desc) {
            desc = document.createElement("meta");
            desc.setAttribute("name", "description");
            document.head.appendChild(desc);
        }
        desc.setAttribute("content", "View your earnings from ad campaigns and subscriptions on Zindua App. Track your income, payouts, and payment references easily.");

        // Open Graph tags
        const ogTags = [
            { property: "og:title", content: "Zindua App | View Earnings" },
            { property: "og:description", content: "View your earnings from ad campaigns and subscriptions on Zindua App." },
            { property: "og:type", content: "website" },
            { property: "og:url", content: "https://zindua.app/user/earnings" },
            { property: "og:image", content: "https://zindua.app/logo192.png" },
        ];

        ogTags.forEach(({ property, content }) => {
            let tag = document.querySelector(`meta[property='${property}']`);
            if (!tag) {
                tag = document.createElement("meta");
                tag.setAttribute("property", property);
                document.head.appendChild(tag);
            }
            tag.setAttribute("content", content);
        });

        // JSON-LD structured data
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Zindua App User Earnings",
            url: "https://zindua.app/user/earnings",
            description: "Track your earnings from ad campaigns and subscriptions on Zindua App.",
            publisher: {
                "@type": "Organization",
                name: "Zindua",
                logo: "https://zindua.app/logo192.png",
                sameAs: [
                    "https://www.instagram.com/zindua.app/",
                    "https://www.linkedin.com/company/zindua-app/",
                    "https://www.tiktok.com/@zindua.app"
                ]
            }
        });
        document.head.appendChild(script);

        return () => {
            document.head.removeChild(script);
        };
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) setUserId(user.uid)
            else setUserId(null)
        })
        return () => unsubscribe()
    }, [])

    useEffect(() => {
        if (!userId) return

        const fetchIncome = async () => {
            try {
                const q = query(
                    collection(db, "income"),
                    where("influencerId", "==", userId),
                    orderBy("timestamp", "desc")
                )

                const incomeSnap = await getDocs(q)
                const incomeData = []

                incomeSnap.forEach(doc => {
                    incomeData.push({ id: doc.id, ...doc.data() })
                })

                const grouped = incomeData.reduce((acc, item) => {
                    if (item.incomeType === "adRequest") acc.adRequest.push(item)
                    else if (item.incomeType === "subscription") acc.subscription.push(item)
                    return acc
                }, { adRequest: [], subscription: [] })

                setGroupedIncome(grouped)
                setLoading(false)
            } catch (error) {
                console.error("Error fetching income:", error)
                setLoading(false)
            }
        }

        fetchIncome()


    }, [userId])


    const handleWithdraw = (id) => {
        navigate("/withdraw");
    }


    const handleView = (item) => {
        if (item.incomeType === "adRequest") navigate(`/view-ad/${item.adID}`)
        else if (item.incomeType === "subscription") navigate(`/view-subs/${item.adID}`)
    }


    const renderCard = (item) => {
        const date = item.timestamp?.toDate?.() || new Date()
        return (
            <div className="income-card" key={item.id}>
                <div className="card-header">
                    <span className="income-amount">KES {Number(item.totalCost).toLocaleString()}</span>
                    <span className={`income-status ${item.payoutStatus}`}>{item.payoutStatus}</span>
                </div>
                <div className="card-details">
                    <span><strong>Payout:</strong> KES {Number(item.payout).toLocaleString()}</span>
                    <span><strong>Date:</strong> {date.toLocaleDateString()} {date.toLocaleTimeString()}</span>
                </div>
                <div className="card-actions">
                    <button className="btn-view" onClick={() => handleView(item)}>
                        {item.incomeType === 'adRequest' ? 'View Ad' : 'View Subscription'}
                    </button>
                    <button className="btn-withdraw" onClick={() => handleWithdraw(item.id)}>Withdraw</button>
                </div>
                <div className="payment-ref">Payment Ref: {item.paymentReference}</div>
            </div>
        )
    }

    return (
        <div className="mainDashboard">
            <div className="desktopOnly"><LeftBar /></div>
            <div className="mainContent">
                <Topbar />
                <section className="userAdsSection">
                    <VerificationStatus />
                    {loading && <Spinner animation="border" />}
                    {!loading && (
                        <Tabs defaultActiveKey="adRequest" id="user-tabs" className="mb-3 custom-tabs">
                            <Tab eventKey="adRequest" title="Ads Income">
                                {groupedIncome.adRequest.length > 0 ? groupedIncome.adRequest.map(renderCard) :
                                    <div className='noSubscDiv'>
                                        <img src={noSubs} className='noSubscImg' alt="" />
                                        <p className="text-center mt-4">No ads income yet.</p>
                                    </div>
                                }
                            </Tab>
                            <Tab eventKey="subscription" title="Subscriptions Income">
                                {groupedIncome.subscription.length > 0 ? groupedIncome.subscription.map(renderCard) :
                                    <div className='noSubscDiv'>
                                        <img src={noSubs} className='noSubscImg' alt="" />
                                        <p className="text-center mt-4">No subscription income yet.</p>
                                    </div>
                                }
                            </Tab>
                        </Tabs>
                    )}
                </section>
            </div>
            <div className="mobileOnly"><BottomNav /></div>
        </div>
    )
}

export default Earnings